import {
  AIAssistantLanguageModeEnum,
  ScreenShareSurfaceEnum,
  SizeEnum
} from "../../core-ui.enums";

// eslint-disable-next-line no-shadow
export enum CustomerParam {
  RATING_REQUIRED = "ratingRequired",
  SCREEN_SHARE_ENABLED = "screenShareEnabled",
  /**
   * @deprecated. merged into SCREEN_SHARE_SURFACE as ScreenShareSurfaceEnum.currentTab.
   * kept so CustomerOptions.create() can migrate and clear it.
   */
  SCREEN_SHARE_CURRENT_TAB = "screenShareCurrentTab",
  SCREEN_SHARE_SURFACE = "screenShareSurface",
  SPEAKER_ENABLED = "speakerEnabled",
  DEFAULT_FACING_MODE_ENVIRONMENT = "defaultFacingModeEnvironment",
  FILE_TRANSFER_ENABLED = "fileTransferEnabled",
  DISCLAIMER_ENABLED = "disclaimerEnabled",
  /** the default copy, shown when there is no translation for the customer's language */
  DISCLAIMER_TEXT = "disclaimerText",
  DISCLAIMER_TEXT_TRANSLATIONS = "disclaimerTextTranslations",
  DISCLAIMER_VERSION = "disclaimerVersion",
  DISCLAIMER_SCROLL_REQUIRED = "disclaimerScrollRequired",
  DISCLAIMER_CHECKBOXES = "disclaimerCheckboxes",
  DEVICE_SETUP_ENABLED = "deviceSetupEnabled",
  TOP_BAR_ENABLED = "topBarEnabled",
  MEDIA_CONTROLS_ENABLED = "mediaControlsEnabled",
  PARTICIPANT_CONTROLS_ENABLED = "participantControlsEnabled",
  MEDIA_CONTROL_SIZE = "mediaControlSize",
  LEAVE_CONTROL_ENABLED = "leaveControlEnabled",
  LEAVE_CONTROL_WIDGET_ENABLED = "leaveControlWidgetEnabled",
  LEAVE_CONNTROL_WIDGET_KIOSK_ENABLED = "leaveControlWidgetKioskEnabled",
  CAMERA_ENABLED = "cameraEnabled",
  MICROPHONE_ENABLED = "microphoneEnabled",
  CAMERA_DEFAULT_ON = "cameraDefaultOn",
  MICROPHONE_DEFAULT_ON = "microphoneDefaultOn",
  DISPLAY_NAME_REQUIRED = "displayNameRequired",
  /**
   * @deprecated. use PublicParam instead
   */
  INTERACTION_DATA_VISIBLE = "interactionDataVisible",
  AGENT_LEFT_TIMEOUT = "agentLeftTimeout",
  MIRROR_USER_CAM_ENABLED = "mirrorUserCamEnabled",
  AUTO_START_SECONDARY_CAMERA = "autoStartSecondaryCamera",
  NUDGE_ENABLED = "nudgeEnabled",
  AI_ASSISTANT_LANGUAGE_MODE = "aiAssistantLanguageMode",
  APPOINTMENT_CHAT_ENABLED = "appointmentChatEnabled",
  NOISE_SUPPRESSION_ENABLED = "noiseSuppressionEnabled",
  BACKGROUND_BLUR_DEFAULT_ON = "backgroundBlurDefaultOn",
}

/** html copy per language, keyed by the language code (ex: 'fr') */
export interface IDisclaimerTranslations {
  [language: string]: string;
}

/** an extra consent the customer ticks on the consent form, on top of the disclaimer text */
export interface IDisclaimerCheckbox {
  id: string;
  /** html. supports basic formatting and links. the default, when there is no translation */
  label: string;
  labelTranslations?: IDisclaimerTranslations;
  /** when true the customer cannot continue unless this is ticked */
  required: boolean;
}

export interface IApplicationCustomerParams {
  [CustomerParam.RATING_REQUIRED]?: boolean;
  [CustomerParam.SCREEN_SHARE_ENABLED]?: boolean;
  [CustomerParam.SCREEN_SHARE_CURRENT_TAB]?: boolean;
  [CustomerParam.SCREEN_SHARE_SURFACE]?: ScreenShareSurfaceEnum;
  [CustomerParam.DEFAULT_FACING_MODE_ENVIRONMENT]?: boolean;
  [CustomerParam.FILE_TRANSFER_ENABLED]?: boolean;
  [CustomerParam.DISCLAIMER_ENABLED]?: boolean;
  [CustomerParam.DISCLAIMER_TEXT]?: string;
  [CustomerParam.DISCLAIMER_TEXT_TRANSLATIONS]?: IDisclaimerTranslations;
  [CustomerParam.DISCLAIMER_VERSION]?: number;
  [CustomerParam.DISCLAIMER_SCROLL_REQUIRED]?: boolean;
  [CustomerParam.DISCLAIMER_CHECKBOXES]?: IDisclaimerCheckbox[];
  [CustomerParam.DEVICE_SETUP_ENABLED]?: boolean;
  [CustomerParam.TOP_BAR_ENABLED]?: boolean;
  [CustomerParam.MEDIA_CONTROLS_ENABLED]?: boolean;
  [CustomerParam.PARTICIPANT_CONTROLS_ENABLED]?: boolean;
  [CustomerParam.MEDIA_CONTROL_SIZE]?: SizeEnum;
  [CustomerParam.LEAVE_CONTROL_ENABLED]?: boolean;
  [CustomerParam.LEAVE_CONTROL_WIDGET_ENABLED]?: boolean;
  [CustomerParam.LEAVE_CONNTROL_WIDGET_KIOSK_ENABLED]?: boolean;
  [CustomerParam.CAMERA_ENABLED]?: boolean;
  [CustomerParam.MICROPHONE_ENABLED]?: boolean;
  [CustomerParam.CAMERA_DEFAULT_ON]?: boolean;
  [CustomerParam.MICROPHONE_DEFAULT_ON]?: boolean;
  [CustomerParam.SPEAKER_ENABLED]?: boolean;
  [CustomerParam.DISPLAY_NAME_REQUIRED]?: boolean;
  [CustomerParam.INTERACTION_DATA_VISIBLE]?: boolean;
  [CustomerParam.AGENT_LEFT_TIMEOUT]?: number;
  [CustomerParam.MIRROR_USER_CAM_ENABLED]?: boolean;
  [CustomerParam.AUTO_START_SECONDARY_CAMERA]?: boolean;
  [CustomerParam.NUDGE_ENABLED]?: boolean;
  [CustomerParam.AI_ASSISTANT_LANGUAGE_MODE]?: AIAssistantLanguageModeEnum;
  [CustomerParam.APPOINTMENT_CHAT_ENABLED]?: boolean;
  [CustomerParam.NOISE_SUPPRESSION_ENABLED]?: boolean;
  [CustomerParam.BACKGROUND_BLUR_DEFAULT_ON]?: boolean;
}

/** ONLY role: CUSTOMER can see this */
export class CustomerOptions implements IApplicationCustomerParams {
  public [CustomerParam.RATING_REQUIRED] = false;
  public [CustomerParam.SCREEN_SHARE_ENABLED] = true;
  public [CustomerParam.SCREEN_SHARE_CURRENT_TAB] = false;
  public [CustomerParam.SCREEN_SHARE_SURFACE] = ScreenShareSurfaceEnum.all;
  public [CustomerParam.DEFAULT_FACING_MODE_ENVIRONMENT] = false;
  public [CustomerParam.FILE_TRANSFER_ENABLED] = false;
  public [CustomerParam.DISCLAIMER_ENABLED] = false;
  public [CustomerParam.DISCLAIMER_TEXT] = null;
  public [CustomerParam.DISCLAIMER_TEXT_TRANSLATIONS]: IDisclaimerTranslations =
    {};
  public [CustomerParam.DISCLAIMER_VERSION] = 0;
  public [CustomerParam.DISCLAIMER_SCROLL_REQUIRED] = false;
  public [CustomerParam.DISCLAIMER_CHECKBOXES]: IDisclaimerCheckbox[] = [];
  public [CustomerParam.DEVICE_SETUP_ENABLED] = true;
  public [CustomerParam.TOP_BAR_ENABLED] = true;
  public [CustomerParam.MEDIA_CONTROLS_ENABLED] = true;
  public [CustomerParam.PARTICIPANT_CONTROLS_ENABLED] = true;
  public [CustomerParam.MEDIA_CONTROL_SIZE] = SizeEnum.small;
  public [CustomerParam.LEAVE_CONTROL_ENABLED] = true;
  public [CustomerParam.LEAVE_CONTROL_WIDGET_ENABLED] = false;
  public [CustomerParam.LEAVE_CONNTROL_WIDGET_KIOSK_ENABLED] = true;
  public [CustomerParam.CAMERA_ENABLED] = true;
  public [CustomerParam.MICROPHONE_ENABLED] = true;
  public [CustomerParam.CAMERA_DEFAULT_ON] = true;
  public [CustomerParam.MICROPHONE_DEFAULT_ON] = true;
  public [CustomerParam.SPEAKER_ENABLED] = true;
  public [CustomerParam.DISPLAY_NAME_REQUIRED] = false;
  public [CustomerParam.INTERACTION_DATA_VISIBLE] = false;
  public [CustomerParam.AGENT_LEFT_TIMEOUT] = null;
  public [CustomerParam.MIRROR_USER_CAM_ENABLED] = true;
  public [CustomerParam.AUTO_START_SECONDARY_CAMERA] = false;
  public [CustomerParam.NUDGE_ENABLED] = false;
  public [CustomerParam.AI_ASSISTANT_LANGUAGE_MODE] =
    AIAssistantLanguageModeEnum.AUTO;
  public [CustomerParam.APPOINTMENT_CHAT_ENABLED] = true;
  public [CustomerParam.NOISE_SUPPRESSION_ENABLED] = false;
  public [CustomerParam.BACKGROUND_BLUR_DEFAULT_ON] = false;

  static create(options: IApplicationCustomerParams) {
    const instance = new CustomerOptions();
    Object.assign(instance, options);

    // the standalone "only share current tab" flag became a surface option. migrate
    // it and clear it, so the next save from the settings screen persists the
    // migration and a later surface change is not overridden by the legacy flag
    if (options?.[CustomerParam.SCREEN_SHARE_CURRENT_TAB]) {
      instance[CustomerParam.SCREEN_SHARE_SURFACE] =
        ScreenShareSurfaceEnum.currentTab;
      instance[CustomerParam.SCREEN_SHARE_CURRENT_TAB] = false;
    }

    return instance;
  }
}

/**
 * The consent copy in the given language, or the default copy when it was not
 * translated. Forms saved before translations existed only have the default.
 */
export function localizeDisclaimer(
  text: string,
  translations: IDisclaimerTranslations,
  language: string
): string {
  const lang = language?.split("-")[0].toLowerCase();
  return (lang && translations?.[lang]) || text;
}
